import type { MemberPurchase } from './member-purchase';
import type { MemberReport } from './member-report';

/**
 * 画面の下の一覧（在庫リストに無い商品）の 1 行。
 * みんなの仕入れ実績（商品共有）と LINE 報告を、在庫一覧と同じ形（在庫状況は無し）で出すための共通の形（2026-10-14 ユーザー指定）
 */
export type MemberRow = {
  key: string;
  /** LINE 報告なら、その報告の id（ユーザ管理の権限がある人の削除ボタンで使う）。仕入れ実績には無い */
  reportId?: number;
  /** 無ければ画像・Keepa グラフ・Amazon へのリンクは出さない（らくじゃんに無い JAN の LINE 報告など） */
  asin: string;
  jan: string;
  imageUrl: string;
  /** らくじゃんに無い商品なら空 */
  productName: string;
  /** JAN / ASIN の下に出す行（例: 「仕入日: 10/9」「仕入れた人: きょうこ」） */
  details: string[];
  boughtPastMonth?: number | null;
  /** 在庫一覧と同じ決め方の Amazon 価格（無い時は -1） */
  amazonPrice: number;
  amazonPriceSource: 'current' | 'last' | 'none' | 'stale';
  lastKnownPriceAt?: string;
  /** 仕入値。LINE 報告で読めなかった時は null */
  cost: number | null;
  /** Amazon 価格で FBA で売った場合の利益額・利益率（在庫一覧と同じ式）。出せない時は null */
  fbaProfitPrice: number | null;
  fbaProfitRate: number | null;
  /** 商品名の下に出す行。左の列（報告した人・店など）と重ならないものだけ（2026-10-15 ユーザー指定）。無ければ空 */
  line: string;
};

const yen = (n: number): string => n.toLocaleString('ja-JP');
const signed = (n: number): string => `${n >= 0 ? '+' : ''}${yen(n)}`;

/** 商品共有の実績 → 一覧の行 */
export function purchaseToRow(m: MemberPurchase): MemberRow {
  return {
    key: `purchase-${m.id}`,
    asin: m.asin,
    jan: m.jan,
    imageUrl: m.imageUrl,
    productName: m.productName,
    details: [`仕入日: ${m.buyingDate}`, `仕入れた人: ${m.userName}`],
    boughtPastMonth: m.boughtPastMonth,
    amazonPrice: m.amazonPrice,
    amazonPriceSource: m.amazonPriceSource,
    lastKnownPriceAt: m.lastKnownPriceAt,
    cost: m.buyingPrice,
    fbaProfitPrice: m.fbaProfitPrice,
    fbaProfitRate: m.fbaProfitRate,
    // 誰が・いつ・仕入値は左の列にあるので、ここは出品予定とその利益だけ
    line: `出品予定 ${yen(m.sellingPrice)}（${signed(m.profitPrice)}）`,
  };
}

/** LINE 報告 → 一覧の行 */
export function reportToRow(r: MemberReport): MemberRow {
  return {
    key: `report-${r.id}`,
    reportId: r.id,
    asin: r.asin,
    jan: r.jan,
    imageUrl: r.imageUrl,
    productName: r.productName,
    details: [`報告日: ${r.reportedAt}`, `報告した人: ${r.memberName}`, ...(r.storeName ? [`店: ${r.storeName}`] : [])],
    boughtPastMonth: r.boughtPastMonth,
    amazonPrice: r.amazonPrice,
    amazonPriceSource: r.amazonPriceSource,
    lastKnownPriceAt: r.lastKnownPriceAt,
    cost: r.price,
    fbaProfitPrice: r.fbaProfitPrice,
    fbaProfitRate: r.fbaProfitRate,
    // 報告した人・日・仕入値・店は左の列に出すので、行は出さない
    line: '',
  };
}

/**
 * 画面の下の一覧（みんなの仕入れ実績・LINE 報告）を、在庫一覧の検索窓と同じキーワードで絞る。
 * JAN・ASIN・商品名・報告の行（誰が・いつ・店）のどれかに含まれていれば残す。大文字小文字は区別しない（2026-10-18 ユーザー指定）
 */
export function filterMemberRows(rows: MemberRow[], keyword: string): MemberRow[] {
  const k = (keyword ?? '').trim().toLowerCase();
  if (!k) {
    return rows;
  }
  return rows.filter((r) =>
    [r.jan, r.asin, r.productName, ...(r.details ?? [])].some((v) => (v ?? '').toLowerCase().includes(k)),
  );
}

const CSV_FIXED_HEAD = ['JAN', 'ASIN', '商品名', 'Amazon価格', '仕入値', '利益額', '利益率', '先月販売数'];

/** CSV の 1 マス。カンマ・改行・" を含む時だけ " で囲む（エクセルがそのまま読める形） */
const csvCell = (v: string | number | null | undefined): string => {
  if (v == null || v === '') {
    return '';
  }
  const s = String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * 画面の下の一覧（みんなの仕入れ実績・LINE 報告）をエクセルで開ける CSV にする（2026-10-19 ユーザー指定）。
 * JAN/ASIN の下に出している「報告日: 10/19」のような行は、並べ替えられるように「報告日」などの列に分ける（出てきた順）。
 * 画面で - と出す所（Amazon 価格なし・仕入値が読めなかった・らくじゃんに無い商品）は空欄にする
 */
export function memberRowsToCsv(rows: MemberRow[]): string {
  const labels: string[] = [];
  const detailsOf = rows.map((r) => {
    const d: Record<string, string> = {};
    for (const s of r.details ?? []) {
      const i = s.indexOf(': ');
      const label = i >= 0 ? s.slice(0, i) : s;
      if (!labels.includes(label)) {
        labels.push(label);
      }
      d[label] = i >= 0 ? s.slice(i + 2) : '';
    }
    return d;
  });
  const header = [...CSV_FIXED_HEAD, ...labels, '備考', 'Amazonリンク'];
  const lines = rows.map((r, i) =>
    [
      r.jan,
      r.asin,
      r.productName,
      r.amazonPriceSource === 'current' || r.amazonPriceSource === 'last' ? r.amazonPrice : '',
      r.cost,
      r.fbaProfitPrice,
      r.fbaProfitRate == null ? '' : `${(r.fbaProfitRate * 100).toFixed(1)}%`,
      r.boughtPastMonth == null ? '' : r.boughtPastMonth < 10 ? '9以下' : r.boughtPastMonth,
      ...labels.map((l) => detailsOf[i][l] ?? ''),
      r.line,
      r.asin ? `https://www.amazon.co.jp/dp/${r.asin}` : '',
    ]
      .map(csvCell)
      .join(','),
  );
  return [header.join(','), ...lines].join('\r\n');
}
